import React, { useState, useContext, useEffect, createContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Modal, Typography } from '@tucar/pit-stop';

const SalesforceContext = createContext();

export const SalesforceContextProvider = ({ children, initializing, initializedChat }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [failedChatInit, setFailedChatInit] = useState(false);
  const [loadingTime, setLoadingTime] = useState(10);
  const [errorModalTime, setErrorModalTime] = useState(5);

  const handleCloseModal = () => {
    setFailedChatInit(false);
    if (loadingTime <= 0) setLoadingTime(10);
  };

  const openSalesforceChat = () => {
    if (initializedChat && !initializing && window?.embeddedservice_bootstrap?.utilAPI) {
      window.embeddedservice_bootstrap.utilAPI.launchChat();
      setFailedChatInit(false);
      setUnreadCount(0);
    } else {
      setFailedChatInit(true);
    }
  };

  const contextValue = useMemo(
    () => ({ openSalesforceChat, unreadCount }),
    [openSalesforceChat, unreadCount],
  );

  useEffect(() => {
    if (loadingTime > 0 && (initializing || loadingTime !== 10)) {
      const timer = setInterval(() => setLoadingTime((prevCount) => prevCount - 1), 1000);
      return () => clearInterval(timer);
    }
    if (loadingTime === 0 && !failedChatInit) setLoadingTime(10);
    return undefined;
  }, [loadingTime, initializing, failedChatInit]);

  useEffect(() => {
    if (errorModalTime > 0 && failedChatInit && loadingTime === 10 && !initializing) {
      const timer = setInterval(() => setErrorModalTime((prevTime) => prevTime - 1), 1000);
      return () => clearInterval(timer);
    }
    if (errorModalTime === 0) {
      setFailedChatInit(false);
      setErrorModalTime(5);
    }
    return undefined;
  }, [loadingTime, errorModalTime, initializing, failedChatInit]);

  return (
    <SalesforceContext.Provider value={contextValue}>
      <Modal
        onClose={handleCloseModal}
        onConfirm={handleCloseModal}
        open={failedChatInit}
        title="Error al iniciar el Chat"
        variant="informative"
      >
        <Typography>
          Hubo un error al iniciar el chat, por favor intenta de nuevo{' '}
          {initializing || loadingTime !== 10 ? (
            <Typography weight="semi-bold">
              {loadingTime > 0 ? `en ${loadingTime} segundos` : 'ahora'}
            </Typography>
          ) : (
            'más tarde o contacta a contacto@tucar.app'
          )}
          .
        </Typography>
      </Modal>
      {children}
    </SalesforceContext.Provider>
  );
};

SalesforceContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
  initializedChat: PropTypes.bool,
  initializing: PropTypes.bool,
};

SalesforceContextProvider.defaultProps = {
  initializedChat: false,
  initializing: false,
};

export const useSalesforceContext = () => useContext(SalesforceContext);
